import type { HTMLInputAutoCompleteAttribute } from 'react';
import { useRouter } from 'next/router';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CompanySizesOptions,
  ContactFormFields,
  ContactFormMetadata,
  DEFAULT_HUBSPOT_CAMPAIGN_ID,
  HUBSPOT_MARKETING_INFORMATION_SUBSCRIPTION_ID,
  isPersonalEmail,
  PartnerTypesOptions,
  type ContactFormFields as TContactFormFields,
} from '@transcend-io/contact-form-schema';
import { useForm, type ControllerRenderProps } from 'react-hook-form';
import { z } from 'zod';

import { ATTRIBUTION_COOKIE_NAME } from '#/lib/attribution-constants';
import type {
  SanityContactForm,
  SanityDefaultForm,
  SanityEnrichedForm,
  SanityFormField,
  SanityFormFieldEmail,
  SanityMultiForm,
} from '#/lib/sanity/queries/form';
import { Input } from '#/components/Input';
import { Listbox } from '#/components/Listbox';
import { Textarea } from '#/components/Textarea';

function getAutoCompleteAttribute(
  fieldName: keyof typeof TContactFormFields.shape,
): HTMLInputAutoCompleteAttribute {
  switch (fieldName) {
    case 'company':
      return 'organization';
    case 'title':
      return 'organization-title';
    case 'firstName':
      return 'given-name';
    case 'lastName':
      return 'family-name';

    default:
      return '';
  }
}

/**
 * Form title + pathname for Segment / HubSpot form_source.
 * Query strings (UTMs, etc.) must not be included — they belong on
 * `conversionUrl` / dedicated utm_* fields and break Slack routing when
 * substring-matched (e.g. `utm_medium=newsletter`).
 */
export function generateAnalyticsId(
  formTitle?: string | null,
  path?: string | null,
) {
  let pathString = path?.split(/[?#]/u)[0];
  if (pathString === '/') {
    pathString = '/index';
  }
  return `${formTitle ?? 'title-unknown'} : ${pathString ?? 'path-unknown'}`;
}

export function getFormFieldComponent(
  fieldData: SanityFormField,
  fieldProps: ControllerRenderProps,
  autoFocus?: boolean,
  theme?: 'light' | 'dark',
) {
  switch (fieldData._type) {
    case 'text':
      return (
        <Input
          theme={theme}
          type={fieldData._type}
          required={fieldData.isRequired}
          placeholder={fieldData.label}
          autoComplete={getAutoCompleteAttribute(
            fieldProps.name as keyof typeof TContactFormFields.shape,
          )}
          {...fieldProps}
          autoFocus={autoFocus}
        />
      );
    case 'email':
      return (
        <Input
          theme={theme}
          type={fieldData._type}
          autoComplete="work email"
          required={fieldData.isRequired}
          placeholder={fieldData.label}
          {...fieldProps}
          autoFocus={autoFocus}
        />
      );
    case 'tel':
      return (
        <Input
          theme={theme}
          type={fieldData._type}
          autoComplete="work tel"
          required={fieldData.isRequired}
          placeholder={fieldData.label}
          {...fieldProps}
          autoFocus={autoFocus}
        />
      );
    case 'textarea':
      return (
        <Textarea
          theme={theme}
          required={fieldData.isRequired}
          placeholder={fieldData.label}
          autoComplete={getAutoCompleteAttribute(
            fieldProps.name as keyof typeof TContactFormFields.shape,
          )}
          {...fieldProps}
          autoFocus={autoFocus}
        />
      );
    case 'select': {
      let options;
      if (fieldData.name === 'partnerType') {
        options = PartnerTypesOptions;
      } else if (fieldData.name === 'companySize') {
        options = CompanySizesOptions;
      }
      return (
        <Listbox
          theme={theme}
          {...fieldProps}
          options={
            options?.map((option) => ({
              label: option,
              value: option,
            })) ?? []
          }
          placeholder={fieldData.label}
        />
      );
    }
    default:
      return null;
  }
}
// If the email field requires a business address, we need to add a validation rule to the form schema
export function addBusinessEmailValidation(
  form:
    | SanityDefaultForm
    | SanityMultiForm
    | SanityEnrichedForm
    | SanityContactForm,
) {
  if (form.formType === 'contact') {
    return ContactFormFields.extend({
      email: ContactFormFields.shape.email.refine(
        (value) => !isPersonalEmail(value),
        {
          message: 'Please enter a work email address.',
        },
      ),
    });
  }

  // Determine the correct field array based on the form type
  const allFields =
    form.formType === 'default'
      ? form.fields
      : form.fieldGroups.flatMap((group) => group.fields);

  // Find the email field within the resolved fields array
  const emailField = allFields.find(
    (field): field is SanityFormFieldEmail =>
      'name' in field && field.name === 'email',
  );

  // Apply business email validation if required
  const formFieldsSchema = emailField?.requireBusinessEmail
    ? ContactFormFields.extend({
        // Validate that the email address is not a personal email
        email: ContactFormFields.shape.email.refine(
          (value) => !isPersonalEmail(value),
          {
            message: 'Please enter a work email address.',
          },
        ),
      })
    : ContactFormFields;

  return formFieldsSchema;
}

interface UseFormMetadataArgs {
  /** The title of the form - used to generate an analytics ID */
  formTitle: SanityDefaultForm['formTitle'];
  /** The path that the form is renderedon */
  path?: string;
  hubspotCampaignId?: SanityDefaultForm['hubspotCampaignId'];
  hubspotConvertingFormType?: SanityDefaultForm['hubspotConvertingFormType'];
  contentPieceTitle?: SanityDefaultForm['contentPieceTitle'];
  hubspotEmailSubscriptionIds?: SanityDefaultForm['hubspotEmailSubscriptionIds'];
  trackingEventName: SanityDefaultForm['trackingEventName'];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formFieldsSchema: z.ZodType<any, any, any>;
  defaultValues?: Partial<TContactFormFields>;
}

interface AttributionCookieData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_id?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  msclkid?: string;
}

/**
 * Reads attribution data from cookie if available
 */
async function getAttributionFromCookie(): Promise<AttributionCookieData | null> {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    if (typeof cookieStore !== 'undefined') {
      const cookie = await cookieStore.get(ATTRIBUTION_COOKIE_NAME);
      if (!cookie || !cookie.value) {
        return null;
      }
      return JSON.parse(cookie.value) as AttributionCookieData;
    }

    const cookies = document.cookie.split('; ');
    const target = cookies.find((c) =>
      c.startsWith(`${ATTRIBUTION_COOKIE_NAME}=`),
    );
    if (!target) return null;
    const value = decodeURIComponent(target.split('=').slice(1).join('='));
    return JSON.parse(value) as AttributionCookieData;
  } catch {
    return null;
  }
}

/**
 * Gets attribution value from URL query param, falling back to cookie data
 */
function getAttributionValue(
  key: keyof AttributionCookieData,
  query: Record<string, string | string[] | undefined>,
  cookieData: AttributionCookieData | null,
): string | undefined {
  const queryValue = query[key];
  return typeof queryValue === 'string' ? queryValue : cookieData?.[key];
}

export const useFormMetadata = (data: UseFormMetadataArgs) => {
  const { query } = useRouter();

  // Null/undefined (form predates the field) falls back to Marketing
  // Information; an explicit empty selection is respected and sends nothing.
  const concatHubspotEmailSubscriptionIds =
    (
      data.hubspotEmailSubscriptionIds ?? [
        HUBSPOT_MARKETING_INFORMATION_SUBSCRIPTION_ID,
      ]
    )
      .filter(Boolean)
      .join(', ') || undefined;

  const methods = useForm<ContactFormFields>({
    defaultValues: data.defaultValues,
    resolver: (values, context, options) => {
      if ('companySize' in values && !values.companySize) {
        values.companySize = undefined;
      }

      return zodResolver(data.formFieldsSchema)(values, context, options);
    },
  });

  const getMetadata = async (
    recaptchaToken?: string,
  ): Promise<ContactFormMetadata> => {
    // Read cookie data when metadata is needed (during form submission)
    const cookieData = await getAttributionFromCookie();

    const attributionData = {
      utm_source: getAttributionValue('utm_source', query, cookieData),
      utm_medium: getAttributionValue('utm_medium', query, cookieData),
      utm_campaign: getAttributionValue('utm_campaign', query, cookieData),
      utm_id: getAttributionValue('utm_id', query, cookieData),
      utm_term: getAttributionValue('utm_term', query, cookieData),
      utm_content: getAttributionValue('utm_content', query, cookieData),
    };

    return {
      hubspotCampaignId: data.hubspotCampaignId ?? DEFAULT_HUBSPOT_CAMPAIGN_ID,
      hubspotConvertingFormType: data.hubspotConvertingFormType ?? undefined,
      contentPieceTitle: data.contentPieceTitle ?? undefined,
      hubspotEmailSubscriptionIds: concatHubspotEmailSubscriptionIds,
      trackingEventName: data.trackingEventName,
      formTitle: data.formTitle,
      ...attributionData,
      analyticsId: generateAnalyticsId(data.formTitle, data.path),
      conversionUrl:
        typeof window !== 'undefined' ? window.location.href : undefined,
      recaptchaToken,
    };
  };

  return { methods, getMetadata };
};
